/**
 * AirspaceMapPage.jsx
 * Original layout — fonts swapped to Bebas Neue + IBM Plex Mono via CSS.
 * Only addition: Google Fonts <link> injected once on mount.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react'
import AirspaceMapHero from '../../components/AirspaceMapHero'
import './AirspacemapPage.css'

/* ── Inject Ecosystem fonts once ─────────────────────────────────── */
function injectFonts() {
    if (document.getElementById('eco-fonts')) return
    const l = document.createElement('link')
    l.id = 'eco-fonts'
    l.rel = 'stylesheet'
    l.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=IBM+Plex+Mono:ital,wght@0,400;0,500;0,600;1,400&family=Cormorant+Garamond:ital,wght@1,400;1,600&display=swap'
    document.head.appendChild(l)
}

/* ── INTERSECTION OBSERVER ───────────────────────────────────────── */
const sectionObserver =
    typeof window !== 'undefined'
        ? new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add('amp-visible')
                        sectionObserver.unobserve(e.target)
                    }
                }),
            { threshold: 0.06, rootMargin: '0px 0px -48px 0px' }
        )
        : null

function Section({ children, className = '', id = '' }) {
    const ref = useRef(null)
    useEffect(() => {
        const el = ref.current
        if (el && sectionObserver) {
            sectionObserver.observe(el)
            return () => sectionObserver.unobserve(el)
        }
    }, [])
    return (
        <section id={id} ref={ref} className={`amp-section ${className}`}>
            {children}
        </section>
    )
}

/* ── DATA ────────────────────────────────────────────────────────── */
const ZONES = [
    {
        name: 'Red Zone',
        tag: 'NO FLY',
        color: '#ef4444',
        glow: 'rgba(239,68,68,.18)',
        bg: 'rgba(239,68,68,.06)',
        border: 'rgba(239,68,68,.22)',
        activeBorder: 'rgba(239,68,68,.55)',
        desc: 'Absolute no-fly airspace. Includes runways, military installations, naval bases, and all permanently restricted areas. Violation results in immediate grounding and legal action under DGCA regulations.',
        items: ['Runways & Airport Perimeters', 'Military & Naval Bases', 'Abandoned Airfields', 'Permanent Restricted Areas'],
        penalty: 'Violation → Immediate grounding + legal action',
    },
    {
        name: 'Yellow Zone',
        tag: 'PERMIT REQUIRED',
        color: '#f59e0b',
        glow: 'rgba(245,158,11,.18)',
        bg: 'rgba(245,158,11,.06)',
        border: 'rgba(245,158,11,.22)',
        activeBorder: 'rgba(245,158,11,.55)',
        desc: 'Controlled airspace requiring prior permission from the relevant AAI office or state authority. All UAS operators must obtain clearance before any flight operation in this zone.',
        items: ['Controlled Terminal Areas', 'AAI Permission Mandatory', 'ATC Coordination Required', 'Flight Plans Must Be Filed'],
        penalty: 'Fly without permit → Penalty under UAS Rules 2021',
    },
    {
        name: 'Green Zone',
        tag: 'PERMITTED',
        color: '#22c55e',
        glow: 'rgba(34,197,94,.18)',
        bg: 'rgba(34,197,94,.06)',
        border: 'rgba(34,197,94,.22)',
        activeBorder: 'rgba(34,197,94,.55)',
        desc: 'Standard DGCA-compliant operations permitted. Drone pilots may fly within defined altitude limits without prior permission. All flights must still be logged and conducted per standard procedures.',
        items: ['Open for UAS Operations', 'DGCA Rules Apply', 'Altitude Limit: 400ft AGL', 'Flight Logs Mandatory'],
        penalty: 'Exceed altitude limits → Log violation filed',
    },
    // {
    //     name: 'Blue Zone',
    //     tag: 'CONDITIONAL',
    //     color: '#3b82f6',
    //     glow: 'rgba(59,130,246,.18)',
    //     bg: 'rgba(59,130,246,.06)',
    //     border: 'rgba(59,130,246,.22)',
    //     activeBorder: 'rgba(59,130,246,.55)',
    //     desc: 'Conditional clearance zones with time and altitude restrictions. Access is subject to ATC availability and defined operating windows. Operators must confirm conditions before each flight.',
    //     items: ['Time Window Restrictions', 'Altitude Caps Apply', 'ATC Approval Required', 'Conditional Access Only'],
    //     penalty: 'Unapproved entry → ATC incident report filed',
    // },
]

const FEATURES = [
    { icon: '🗺️', title: '3D Interactive Map', desc: 'Visualise India\'s entire airspace in three dimensions. Tilt, rotate, and zoom from ground level to Class A airspace.', accent: '#22c55e' },
    { icon: '🔍', title: 'Search & Filter', desc: 'Find any airport, city, or facility instantly. Filter by zone type, state, or facility category in real time.', accent: '#3b82f6' },
    { icon: '📋', title: 'Permit Portal', desc: 'Submit permission requests to AAI, state authorities, and ATC directly from the platform. Track your status in one place.', accent: '#f59e0b' },
    { icon: '📁', title: 'KML File Import', desc: 'Upload your own KML files to overlay custom airspace boundaries and operational zones on the live map.', accent: '#8b5cf6' },
    { icon: '🛠️', title: 'Admin Dashboard', desc: 'Manage facility data, review permit requests, and update airspace information via the admin interface.', accent: '#ef4444' },
    { icon: '💬', title: 'Feedback System', desc: 'Pilots submit zone accuracy reports. Admins review and action them — keeping the map always current.', accent: '#06b6d4' },
]

const STEPS = [
    { num: '01', title: 'Open the Map', desc: 'Go to airspacemap.in. No install needed — runs entirely in the browser. The full India airspace map loads instantly.' },
    { num: '02', title: 'Find Your Zone', desc: 'Search your city or drop a pin. Filter by zone type. Know instantly whether you\'re in Red, Yellow, Green, or Blue.' },
    { num: '03', title: 'Request or Fly', desc: 'Green zone? You\'re cleared within altitude limits. Yellow zone? File a permit request directly from the platform.' },
]

const FACILITIES = [
    { label: 'Airports', color: '#374151' },
    { label: 'Runway', color: '#ef4444' },
    { label: 'Military & Naval Base', color: '#dc2626' },
    { label: 'Abandoned Airfields', color: '#b91c1c' },
    { label: 'Temporary Red Zone', color: '#f43f5e' },
    { label: 'Helipad Base', color: '#db2777' },
    { label: 'Green Zone', color: '#16a34a' },
    { label: 'Boundary Base', color: '#b45309' },
    { label: 'States Boundary', color: '#d97706' },
    { label: 'Others', color: '#6b7280' },
]

/* ── INTERACTIVE ZONE GRID ────────────────────────────────────────── */
function ZoneGrid() {
    const [active, setActive] = useState(null)

    const toggle = useCallback((i) =>
        setActive((prev) => (prev === i ? null : i)),
        [])

    return (
        <div className="amp-zones-grid">
            {ZONES.map((z, i) => {
                const isActive = active === i
                const isDimmed = active !== null && !isActive

                return (
                    <div
                        key={i}
                        className={`amp-zone-card${isActive ? ' amp-zone-active' : ''}${isDimmed ? ' amp-zone-dimmed' : ''}`}
                        style={{
                            background: isActive ? z.bg : 'var(--am-card)',
                            borderColor: isActive ? z.activeBorder : z.border,
                            '--zone-glow': z.glow,
                            '--zone-color': z.color,
                        }}
                        onClick={() => toggle(i)}
                        role="button"
                        aria-expanded={isActive}
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && toggle(i)}
                    >
                        <div className="amp-zone-accent-bar" style={{ background: z.color }} />

                        <div className="amp-zone-top">
                            <div className="amp-zone-dot-label">
                                <div className="amp-zone-dot" style={{ background: z.color }} />
                                <div className="amp-zone-name">{z.name}</div>
                            </div>
                            <div className="amp-zone-right">
                                <div className="amp-zone-tag" style={{ color: z.color, borderColor: z.border }}>
                                    {z.tag}
                                </div>
                                <div className={`amp-zone-chevron${isActive ? ' rotated' : ''}`}
                                    style={{ color: z.color }}>
                                    ›
                                </div>
                            </div>
                        </div>

                        <p className="amp-zone-desc">{z.desc}</p>

                        <div className="amp-zone-items">
                            {z.items.map((item, j) => (
                                <div key={j} className="amp-zone-item">
                                    <div className="amp-zone-item-dot" style={{ background: z.color }} />
                                    {item}
                                </div>
                            ))}
                        </div>

                        <div className={`amp-zone-expand${isActive ? ' open' : ''}`}>
                            <div className="amp-zone-expand-inner">
                                <div className="amp-zone-penalty" style={{ borderColor: z.border, color: z.color }}>
                                    <span className="amp-zone-penalty-icon">⚠</span>
                                    {z.penalty}
                                </div>
                                <a href="https://airspacemap.in" target="_blank" rel="noreferrer"
                                    className="amp-zone-cta-link"
                                    style={{ color: z.color }}
                                    onClick={(e) => e.stopPropagation()}>
                                    View on Map →
                                </a>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

/* ── ANIMATED STEPS ───────────────────────────────────────────────── */
function StepsSection() {
    const ref = useRef(null)
    const [progress, setProgress] = useState(0)

    useEffect(() => {
        const el = ref.current
        if (!el) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    let start = null
                    const duration = 900

                    const tick = (ts) => {
                        if (!start) start = ts
                        const p = Math.min((ts - start) / duration, 1)
                        setProgress(1 - Math.pow(1 - p, 3))
                        if (p < 1) requestAnimationFrame(tick)
                    }
                    requestAnimationFrame(tick)
                    observer.disconnect()
                }
            },
            { threshold: 0.4 }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    return (
        <div className="amp-steps" ref={ref}>
            <div className="amp-steps-line-track">
                <div className="amp-steps-line-fill"
                    style={{ transform: `scaleX(${progress})` }} />
            </div>

            {STEPS.map((s, i) => (
                <div key={i} className="amp-step" style={{ animationDelay: `${i * 0.12}s` }}>
                    <div className="amp-step-num"
                        style={{
                            borderColor: 'rgba(34,197,94,.35)', color: '#22c55e',
                            boxShadow: progress > (i / STEPS.length)
                                ? '0 0 0 4px rgba(34,197,94,.12)' : 'none'
                        }}>
                        {s.num}
                    </div>
                    <div className="amp-step-title">{s.title}</div>
                    <p className="amp-step-desc">{s.desc}</p>
                </div>
            ))}
        </div>
    )
}

/* ── PAGE ────────────────────────────────────────────────────────── */
export default function AirspaceMapPage() {
    useEffect(() => {
        injectFonts()
        document.title = "AirspaceMap | Interactive Drone No-Fly Zones in India"
        let metaDesc = document.querySelector('meta[name="description"]')
        if (metaDesc) {
            metaDesc.setAttribute("content", "AirspaceMap by Aviatricks: The definitive interactive map for drone no-fly zones in India. Check Red, Yellow, Green before you fly.")
        }
    }, [])

    return (
        <>
            <AirspaceMapHero />

            {/* 1. ABOUT */}
            <Section className="amp-about" id="about">
                <div className="amp-container amp-about-grid">
                    <div className="amp-about-text">
                        <div className="amp-chip"
                            style={{ color: '#22c55e', borderColor: 'rgba(34,197,94,.25)', background: 'rgba(34,197,94,.06)' }}>
                            About the Platform
                        </div>
                        <h2 className="amp-h2">
                            India's Airspace,<br />
                            <em style={{ backgroundImage: 'linear-gradient(135deg,#22c55e,#3b82f6)' }}>
                                Mapped Precisely
                            </em>
                        </h2>
                        <p className="amp-body">
                            AirspaceMap is a free web platform built by Aviatricks Aero Labs to give
                            every drone pilot in India clear, reliable information about where they can
                            and cannot fly. It maps airports, military bases, naval installations,
                            helipads, and restricted areas across the entire country — all colour-coded
                            by DGCA zone classification.
                        </p>
                        <p className="amp-body">
                            Whether you're a hobbyist confirming your backyard is in a green zone or an
                            enterprise operator managing a fleet across multiple states, AirspaceMap
                            gives you the same clear picture — free, instantly, in any browser.
                        </p>
                        <div className="amp-pills">
                            {['Free to Use', 'DGCA Zone Data', 'Pan-India Coverage', 'Browser-Based'].map((p, i) => (
                                <span key={i} className="amp-pill">{p}</span>
                            ))}
                        </div>
                    </div>

                    <div className="amp-product-card-wrap">
                        <div className="amp-product-card">
                            <div className="amp-product-org">Aviatricks Aero Labs</div>
                            <div className="amp-product-name">AirspaceMap</div>
                            <a href="https://airspacemap.in" target="_blank" rel="noreferrer"
                                className="amp-product-url">airspacemap.in →</a>
                            <div className="amp-status-badge">
                                <span className="amp-status-dot" />
                                Live — Free Access
                            </div>

                            <div className="amp-card-legend">
                                {ZONES.map((z, i) => (
                                    <div key={i} className="amp-card-legend-item">
                                        <div className="amp-card-legend-dot" style={{ background: z.color }} />
                                        <span>{z.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <div className="amp-rule" />

            {/* 2. ZONE CLASSIFICATION */}
            <Section className="amp-zones-section" id="zones">
                <div className="amp-container">
                    <div className="amp-sec-head amp-center">
                        <div className="amp-chip"
                            style={{ color: '#f59e0b', borderColor: 'rgba(245,158,11,.25)', background: 'rgba(245,158,11,.06)' }}>
                            DGCA Zone Classification
                        </div>
                        <h2 className="amp-h2">
                            Three Colours.{' '}
                            <em style={{ backgroundImage: 'linear-gradient(135deg,#ef4444,#f59e0b,#22c55e,#3b82f6)' }}>
                                Three Sets of Rules.
                            </em>
                        </h2>
                        <p className="amp-body amp-max560">
                            India's airspace is divided into three zone types under the UAS Rules 2021.
                            Click any zone to see full details and consequences.
                        </p>
                    </div>
                    <ZoneGrid />
                </div>
            </Section>

            <div className="amp-rule" />

            {/* 3. FEATURES */}
            <Section className="amp-features" id="features">
                <div className="amp-container">
                    <div className="amp-sec-head">
                        <div className="amp-chip"
                            style={{ color: '#3b82f6', borderColor: 'rgba(59,130,246,.25)', background: 'rgba(59,130,246,.06)' }}>
                            Platform Features
                        </div>
                        <h2 className="amp-h2">
                            Everything a Drone{' '}
                            <em style={{ backgroundImage: 'linear-gradient(135deg,#3b82f6,#22c55e)' }}>
                                Operator Needs
                            </em>
                        </h2>
                        <p className="amp-body amp-max480">
                            Built for everything from a quick zone check to enterprise fleet management.
                        </p>
                    </div>

                    <div className="amp-features-grid">
                        {FEATURES.map((f, i) => (
                            <div key={i} className="amp-feat-card"
                                style={{ '--feat-accent': f.accent, animationDelay: `${i * 0.07}s` }}>
                                <span className="amp-feat-icon">{f.icon}</span>
                                <div className="amp-feat-title">{f.title}</div>
                                <p className="amp-feat-desc">{f.desc}</p>
                                <div className="amp-feat-bar" style={{ background: f.accent }} />
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <div className="amp-rule" />

            {/* 4. HOW IT WORKS */}
            <Section className="amp-how" id="how-it-works">
                <div className="amp-container">
                    <div className="amp-sec-head amp-center">
                        <div className="amp-chip"
                            style={{ color: '#22c55e', borderColor: 'rgba(34,197,94,.25)', background: 'rgba(34,197,94,.06)' }}>
                            How It Works
                        </div>
                        <h2 className="amp-h2">
                            Check, Confirm,{' '}
                            <em style={{ backgroundImage: 'linear-gradient(135deg,#22c55e,#06b6d4)' }}>
                                Fly.
                            </em>
                        </h2>
                        <p className="amp-body amp-max480">
                            No account. No app install. No waiting. Open the map and get your answer in seconds.
                        </p>
                    </div>
                    <StepsSection />
                </div>
            </Section>

            <div className="amp-rule" />

            {/* 5. FACILITY TYPES */}
            <Section className="amp-facilities" id="facilities">
                <div className="amp-container">
                    <div className="amp-sec-head amp-center">
                        <div className="amp-chip"
                            style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,.25)', background: 'rgba(239,68,68,.06)' }}>
                            Mapped Facility Types
                        </div>
                        <h2 className="amp-h2">
                            Every Type of{' '}
                            <em style={{ backgroundImage: 'linear-gradient(135deg,#ef4444,#f59e0b)' }}>
                                Restricted Facility
                            </em>
                        </h2>
                        <p className="amp-body amp-max560">
                            Each facility type has its own colour on the map. Every dot tells you exactly what you're looking at.
                        </p>
                    </div>

                    <div className="amp-fac-grid">
                        {FACILITIES.map((f, i) => (
                            <div key={i} className="amp-fac-chip"
                                style={{ '--fac-color': f.color, '--fac-glow': `${f.color}22`, animationDelay: `${i * 0.05}s` }}>
                                <div className="amp-fac-dot" style={{ background: f.color }} />
                                <div className="amp-fac-label">{f.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>
        </>
    )
}